import React, { useState, useEffect } from 'react';
import { ScrollArea } from './ui/scroll-area';
import { ServiceCard } from './ServiceCard';
import { PackageCard } from './PackageCard';
import { DefaultState } from './DefaultState';
import { DynamicForm } from './DynamicForm';
import { services, packages } from '../data/mock';
import { Separator } from './ui/separator';
import { ArrowRight, Sparkles } from 'lucide-react';

export const EliteBGrowthApp = () => {
  const [selectedServices, setSelectedServices] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);

  // When a package is selected, auto-select its services
  useEffect(() => {
    if (selectedPackage) {
      const pkg = packages.find(p => p.id === selectedPackage);
      if (pkg && pkg.services.length > 0) {
        setSelectedServices(pkg.services);
      } else if (pkg && pkg.id === 'custom') {
        setSelectedServices([]);
      }
    }
  }, [selectedPackage]);

  const handleServiceToggle = (serviceId) => {
    setSelectedServices(prev => {
      if (prev.includes(serviceId)) {
        return prev.filter(id => id !== serviceId);
      } else {
        return [...prev, serviceId];
      }
    });
    // Clear package selection when manually toggling services
    setSelectedPackage(null);
  };

  const handlePackageSelect = (packageId) => {
    if (selectedPackage === packageId) {
      setSelectedPackage(null);
      setSelectedServices([]);
    } else {
      setSelectedPackage(packageId);
    }
  };

  const hasSelection = selectedServices.length > 0 || selectedPackage === 'custom';

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-[1600px] mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-emerald-600 flex items-center justify-center">
              <Sparkles className="h-4 w-4 text-white" />
            </div>
            <div>
              <h1 className="text-base font-bold text-slate-800 tracking-tight">EliteBGrowth</h1>
              <p className="text-[10px] text-slate-500 -mt-0.5">IT Services & Solutions</p>
            </div>
          </div>
          <nav className="hidden md:flex items-center gap-4 text-xs">
            <a href="#services" className="text-slate-600 hover:text-emerald-600 transition-colors">Services</a>
            <a href="#packages" className="text-slate-600 hover:text-emerald-600 transition-colors">Packages</a>
          </nav>
          <a
            href="#form"
            className="hidden md:inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 text-white text-xs font-medium rounded-lg hover:bg-emerald-700 transition-colors"
          >
            Start My Project
            <ArrowRight className="h-3 w-3" />
          </a>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-[1600px] mx-auto">
        <div className="flex min-h-[calc(100vh-56px)]">
          {/* Left Sidebar - Increased width */}
          <aside className="w-[480px] flex-shrink-0 border-r border-slate-200 bg-white">
            <ScrollArea className="h-[calc(100vh-56px)]">
              <div className="p-4">
                {/* Services Section */}
                <div id="services">
                  <div className="flex items-center justify-between mb-3">
                    <h2 className="text-xs font-semibold text-slate-800 uppercase tracking-wider">
                      Services
                    </h2>
                    {selectedServices.length > 0 && (
                      <span className="text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full font-medium">
                        {selectedServices.length} selected
                      </span>
                    )}
                  </div>
                  <p className="text-[10px] text-slate-500 mb-3">
                    Select individual services or combine them
                  </p>
                  <div className="grid grid-cols-2 gap-1">
                    {services.map(service => (
                      <ServiceCard
                        key={service.id}
                        service={service}
                        isSelected={selectedServices.includes(service.id)}
                        onToggle={handleServiceToggle}
                      />
                    ))}
                  </div>
                </div>

                <Separator className="my-4" />

                {/* Packages Section */}
                <div id="packages">
                  <h2 className="text-xs font-semibold text-slate-800 uppercase tracking-wider mb-2">
                    Ready-Made Packages
                  </h2>
                  <p className="text-[10px] text-slate-500 mb-3">
                    Or pick a curated package with bundled savings
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    {packages.map(pkg => (
                      <PackageCard
                        key={pkg.id}
                        pkg={pkg}
                        isSelected={selectedPackage === pkg.id}
                        onSelect={handlePackageSelect}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </ScrollArea>
          </aside>

          {/* Right Content Area */}
          <div id="form" className="flex-1 bg-slate-50">
            {hasSelection ? (
              <DynamicForm
                selectedServices={selectedServices}
                selectedPackage={selectedPackage}
              />
            ) : (
              <DefaultState />
            )}
          </div>
        </div>
      </main>
    </div>
  );
};
